'use client'

import * as React from 'react'
import { CommitIcon } from '@radix-ui/react-icons'
import { clsx } from 'clsx'
import { format } from 'date-fns'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { Commit as TCommit, isCommit } from '@basehub/data/replicache/index'
import { ScrollArea } from '~/sotano-kit/primitives/scroll-area'
import {
  useBranchOrCommitSegment,
  useSelectedBranch,
  useSelectedBranchOrCommitQuery,
} from '~/lib/commits/use-selected-branch-or-commit'
import { ButtonLink } from '~/sotano-kit/primitives/button'
import { parseTab, useTabsStore } from '../../../explore/components/tabs/store'
import { useIsGuestInHisBranch } from '~/hooks-data/use-user-role'
import { useWorkingDiff } from '~/lib/diff/use-working-diff'
import { usePathname } from 'next/navigation'
import { useCommits } from '~/hooks-data/use-commits'
import { PrimitiveCommit } from './primitive-commit'
import { Filters } from '../../../explore/components/properties-panel/filters'
import { useBranchQuery } from '~/lib/branches/use-branch'
import { FiltersProvider, useFilters, FiltersContext } from './filters'
const HOUR_REGEX_TRIM = /T.*$/

export const CommitsTab = () => {
  const { members, range, setMembers, setRange } = useFilters()

  return (
    <>
      <div className="flex items-center gap-x-4 px-4 mt-3 mb-[7px]">
        <h5 className="text-dark-dim text-sm font-medium grow">History</h5>
        <Filters
          size="icons"
          membersProps={{ selection: members, handleSelection: setMembers }}
          rangeProps={{ selection: range, handleSelection: setRange }}
        />
      </div>
      <hr className="mx-4 border-dark-border-solid" />
      <ScrollArea>
        <div className="px-4 pt-2 pb-3">
          <FiltersProvider members={members} range={range}>
            <CommitsList />
          </FiltersProvider>
        </div>
      </ScrollArea>
    </>
  )
}

const CommitsList = () => {
  const { data: branchOrCommit, isLoading } = useSelectedBranchOrCommitQuery()
  const [max, setMax] = React.useState(20)

  if (isLoading) {
    return (
      <span className="flex items-center text-xs text-dark-muted">
        Commits are loading
      </span>
    )
  }

  if (!branchOrCommit) return <CommitEmptyState />

  if (isCommit(branchOrCommit)) {
    return (
      <CommitsListUI commitId={branchOrCommit.id} max={max} setMax={setMax} />
    )
  }

  if (!branchOrCommit?.headCommitId) {
    return <CommitEmptyState />
  }

  return (
    <CommitsListUI
      commitId={branchOrCommit.headCommitId}
      max={max}
      setMax={setMax}
    />
  )
}

const CommitEmptyState = () => {
  const { teamSlug, repoSlug } = useDashboardSlugs()
  const isGuestInHisBranch = useIsGuestInHisBranch()
  const workingDiff = useWorkingDiff()
  const pathname = usePathname()
  const rest = pathname.split('/').slice(4).join('/')

  if (!isGuestInHisBranch) {
    return (
      <p className="block text-xs leading-tight text-dark-muted text-pretty">
        No commits yet.
      </p>
    )
  }

  return (
    <span className="block text-xs leading-tight text-dark-muted text-pretty">
      No commits yet, go back to the{' '}
      {Boolean(workingDiff.diff?.changesCount) ? (
        <>
          commit your{' '}
          <ButtonLink
            href={`/${teamSlug}/${repoSlug}/changes/${rest}`}
            unstyled
            focusStyled
            className="cursor-pointer text-dark-control-base hover:underline"
          >
            changes
          </ButtonLink>
          .
        </>
      ) : (
        <>
          go back to the{' '}
          <ButtonLink
            href={`/${teamSlug}/${repoSlug}/explore/${rest}`}
            unstyled
            focusStyled
            className="cursor-pointer text-dark-control-base hover:underline"
          >
            explorer
          </ButtonLink>
          .
        </>
      )}
    </span>
  )
}

const CommitsListUI = ({
  commitId: headCommitId,
  max,
  setMax,
}: {
  commitId: string
  max: number
  setMax: React.Dispatch<React.SetStateAction<number>>
}) => {
  const commits = useCommits(headCommitId).data
  const { members, range } = React.useContext(FiltersContext)

  // Filter commits based on members and date range
  const filteredCommits = React.useMemo(() => {
    if (!commits) return []
    return commits.filter((commit) => {
      // Filter by members if any are selected
      if (members.length > 0) {
        const commitContributors = [
          commit.authorId,
          ...(commit.contributors ?? []),
        ]
        if (
          !members.some((memberId) => commitContributors.includes(memberId))
        ) {
          return false
        }
      }

      // Filter by date range if selected
      if (range?.from && range?.to) {
        const commitDate = commit.createdAt.replace(HOUR_REGEX_TRIM, '')
        return (
          commitDate >= range.from.toISOString().replace(HOUR_REGEX_TRIM, '') &&
          commitDate <= range.to.toISOString().replace(HOUR_REGEX_TRIM, '')
        )
      }

      return true
    })
  }, [commits, members, range])

  const { repoSlug, teamSlug } = useDashboardSlugs()
  const { data: branch } = useBranchQuery({ id: commits?.[0]?.branchId ?? '' })
  const headCommitIdFromBranch = React.useMemo(
    () => branch?.headCommit?.id ?? branch?.headCommitId,
    [branch]
  )

  if (!filteredCommits.length) {
    return (
      <div className="flex flex-col text-center pt-1 gap-y-2 items-center pb-3">
        <div className="relative border border-dark-border-solid bg-dark-shade rounded-full size-8 text-dark-dim">
          <CommitIcon className="absolute -translate-x-1/2 -translate-y-1/2 shrink-0 top-1/2 left-1/2" />
        </div>
        <span className="text-dark-muted text-xs leading-normal text-pretty">
          {Boolean(members.length || range)
            ? 'No commits match your current filters.'
            : 'Changes you make will appear here once committed.'}
        </span>
      </div>
    )
  }

  const hasMore = filteredCommits.length > max
  const visibleCommits = filteredCommits.slice(0, max)

  // Group commits by date
  const commitsByDate = Object.entries(
    visibleCommits.reduce<Record<string, TCommit[]>>((acc, commit) => {
      const dateString = formatCommitDate(new Date(commit.createdAt))
      if (!acc[dateString]) {
        acc[dateString] = []
      }
      acc[dateString]?.push(commit)
      return acc
    }, {})
  )

  return (
    <>
      {commitsByDate.map(([dateString, commits], index) => (
        <React.Fragment key={dateString}>
          {index === 0 &&
            headCommitIdFromBranch &&
            headCommitIdFromBranch !== commits[0]?.id && (
              <span className="block text-dark-faint text-[0.75rem] font-medium text-pretty leading-snug -mb-4">
                There are newer commits hidden.{' '}
                {branch && (
                  <ButtonLink
                    href={`/${teamSlug}/${repoSlug}/history/${branch.name}`}
                    unstyled
                    focusStyled
                    className="hover:underline cursor-default"
                  >
                    Go back to "{branch.name}" to see the full list.
                  </ButtonLink>
                )}
              </span>
            )}
          <p className="flex items-center mt-6 -mb-1 space-x-2 text-xs font-medium text-dark-muted first:mt-0">
            <CommitIcon /> <span>{dateString}</span>
          </p>
          {commits.map((commit) => (
            <CommitUI key={commit.id} commit={commit} />
          ))}
        </React.Fragment>
      ))}
      {hasMore && (
        <button
          className="flex items-center justify-center w-full py-2 mt-1 text-xs border rounded-md cursor-default text-dark-muted hover:text-dark-dim border-dark-border-solid hover:border-x-dark-elevated"
          onClick={() => setMax((p) => p + 10)}
        >
          Show more
        </button>
      )}
    </>
  )
}

const formatCommitDate = (date: Date) => format(date, 'MMMM d, yyyy')

const CommitUI = ({ commit }: { commit: TCommit }) => {
  const { teamSlug, repoSlug } = useDashboardSlugs()
  const { encodedSegment, segment } = useBranchOrCommitSegment()
  const linkRef = React.useRef<HTMLAnchorElement>(null)
  const addTab = useTabsStore((s) => s.addTab)
  const { branch } = useSelectedBranch()
  const activeTab = useTabsStore((s) => s.activeTab)
  const parsedActiveTab = activeTab ? parseTab(activeTab) : null
  const isActive =
    parsedActiveTab?.type === 'commit' && parsedActiveTab.commitId === commit.id

  React.useEffect(() => {
    if (isActive && linkRef.current) {
      linkRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      })
    }
  }, [isActive])

  const isCurrentHead = React.useMemo(() => {
    if (branch?.rollback?.commitId === commit.id) return true
    if (!branch?.rollback?.commitId && branch?.headCommitId === commit.id) {
      return true
    }
    return false
  }, [branch?.headCommitId, branch?.rollback?.commitId, commit.id])

  const isCheckingOutThisCommit = segment === commit.id

  return (
    <ButtonLink
      ref={linkRef}
      unstyled
      focusStyled
      data-active={isActive}
      href={`/${teamSlug}/${repoSlug}/history/${encodedSegment}/${commit.id}`}
      onClick={() =>
        addTab({ key: `commit:${commit.id}`, partiallyOpen: true })
      }
      onDoubleClick={() =>
        addTab({ key: `commit:${commit.id}`, partiallyOpen: false })
      }
      className={clsx(
        'group my-2 flex flex-wrap cursor-default scroll-m-4 rounded-md p-2 pt-1',
        isActive ? 'bg-dark-shade-hover' : 'hover:bg-dark-border',
        '-mx-2 pr-2'
      )}
    >
      {isCurrentHead && (
        <span className="rounded uppercase mt-1 mb-1 mr-1.5 w-fit bg-dark-control-base bg-opacity-15 font-semibold text-dark-control-base text-[0.6875rem] leading-normal pt-px px-1.5">
          Current API
        </span>
      )}
      {isCheckingOutThisCommit && (
        <span className="rounded uppercase mt-1 mb-1 w-fit bg-dark-control-base bg-opacity-15 font-semibold text-dark-control-base text-[0.6875rem] leading-normal pt-px px-1.5">
          Viewing now
        </span>
      )}
      <PrimitiveCommit
        contributors={{
          ids: [commit.authorId, ...(commit.contributors ?? [])],
          size: 'petite',
        }}
        message={commit.message}
        date={commit.createdAt}
        id={commit.id}
        className="w-full"
        disableHover
        sidePreference="right"
      />
    </ButtonLink>
  )
}
